import { getAccountType } from '@account/account-portal/lib'
import { AccountType, LightRegistrationData, RegistrationData } from '@account/account-portal/types'
import { useConfig } from '@crystal/config'
import { useFormValidation } from '@crystal/form'
import { useTranslations } from '@crystal/translator'
import { useEffect, useState } from 'react'

import { AccountPortalConfig } from '../../config'
import { useViewData } from '../../lib'

export function useFormData<T extends RegistrationData | LightRegistrationData>(): ReturnType<
  typeof useFormValidation<T>
> {
  const validator = useFormValidation<T>()
  const { getValues, setValue, trigger } = validator
  const { viewData, updateViewData } = useViewData()
  const prefillData = viewData.pageContext?.prefillData
  const { persistentLogin, minAgeConfirmation, ageConfirmation } = getValues() as RegistrationData &
    LightRegistrationData

  useEffect(() => {
    if (!prefillData) {
      return
    }

    Object.entries(prefillData).forEach(([field, prop]) => {
      setValue(field as never, prop.value)
      trigger(field as never)
    })
  }, [prefillData])

  useEffect(() => {
    updateViewData({
      minAgeConfirmation: minAgeConfirmation || ageConfirmation,
      persistentLogin: persistentLogin ?? viewData.persistentLogin,
    })
  }, [persistentLogin, ageConfirmation, minAgeConfirmation])

  return validator
}

export const useFullAccountValidation = (validator: ReturnType<typeof useFormValidation<RegistrationData>>) => {
  const t = useTranslations()
  const [accountType, setAccountType] = useState<AccountType>()
  const [fakeToggle, forceRender] = useState(false)
  const { passwordPolicyRegistration, passwordPolicyLogin } = useConfig<AccountPortalConfig>()
  const { getFieldState, getValues, trigger } = validator

  useEffect(() => {
    forceRender(!fakeToggle)
  }, [validator.formState.errors])

  const onEmailBlur = async () => {
    await trigger('email')
    if (getFieldState('email').invalid) {
      return
    }

    const email = getValues('email')
    const accountResponse = await getAccountType(email).catch(() => undefined)
    if (accountResponse?.accountType) {
      setAccountType(accountResponse.accountType)
      trigger('password')
      trigger('ageConfirmation')
    }
  }

  const getPasswordError = () => {
    if (!getFieldState('password').error) {
      return undefined
    }
    switch (accountType) {
      case AccountType.NotRegistered:
        return t('password.error')
      case AccountType.Full:
        return t('password.hint')
      case AccountType.Light:
        return ''
      default:
        return t('password.error')
    }
  }

  const getPasswordPolicy = () => {
    let policy: Record<string, unknown> = passwordPolicyRegistration
    if (!accountType) {
      return (policy = {})
    }
    switch (accountType) {
      case AccountType.NotRegistered:
        policy = passwordPolicyRegistration
        break
      case AccountType.Full:
        policy = passwordPolicyLogin
        break
      case AccountType.Light:
        policy = {}
        break
    }

    return {
      required: false,
      minLength: 0,
      pattern: /.*/,
      ...policy,
    }
  }

  return {
    onEmailBlur,
    accountType,
    passwordError: getPasswordError(),
    passwordPolicy: getPasswordPolicy(),
  }
}
